// THE CEMETERY — one headstone for every citizen lost, on the fringe of the kingdom just outside the field, on the side nearest
// the camera so it is seen. The count comes from the kingdom's graves (seasonManager.getGraves; written whenever a citizen is
// lost: starved, turned feral, died in the Hospital, shot…). Up to CEMETERY_MAX_STONES stones are shown in rows; the full count is
// in the Registry's "lost" column and in the chip tooltip. Rebuilt on every loss, kingdom switch or load.
import * as THREE from 'three'
import { Events, on } from '../ui/eventBus.js'
import { FIELD_RADIUS, GROUND_Y, axialToWorld, key } from './hexGrid.js'
import { getGraves, getGravesTotal } from '../game/seasonManager.js'

// The fringe tile the cemetery sits on (just outside the field, towards the camera); surroundings.js leaves it clear of bamboo.
export const CEMETERY_TILE = Object.freeze({ q: FIELD_RADIUS + 1, r: 0 })
export const CEMETERY_KEY = key(CEMETERY_TILE.q, CEMETERY_TILE.r)
export const CEMETERY_MAX_STONES = 60

const STONE_MAT = new THREE.MeshLambertMaterial({ color: 0x8e8c84 })
const STONE_DARK = new THREE.MeshLambertMaterial({ color: 0x6e6c66 })
const SOIL_MAT = new THREE.MeshLambertMaterial({ color: 0x4b3a2a })
const PLOT_MAT = new THREE.MeshLambertMaterial({ color: 0x5a6b3a })

let group = null
let sceneRef = null

function headstone(i) {
  const g = new THREE.Group()
  const w = 0.075, h = 0.13, d = 0.028
  const slab = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), i % 7 === 3 ? STONE_DARK : STONE_MAT)
  slab.position.y = h / 2
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(w / 2, w / 2, d, 10, 1, false, 0, Math.PI), slab.material) // the rounded top
  cap.rotation.x = Math.PI / 2
  cap.rotation.z = Math.PI / 2
  cap.position.y = h
  const mound = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 5), SOIL_MAT)
  mound.scale.set(1, 0.35, 1.6)
  mound.position.set(0, 0.004, 0.07)
  g.add(slab, cap, mound)
  g.rotation.y = ((i * 37) % 11 - 5) * 0.02 // each one leans a little its own way
  return g
}

function rebuild() {
  if (!group) return
  for (let i = group.children.length - 1; i >= 0; i--) group.remove(group.children[i])
  const total = getGravesTotal()
  group.visible = total > 0
  if (!total) return
  const { x, z } = axialToWorld(CEMETERY_TILE.q, CEMETERY_TILE.r)
  const n = Math.min(total, CEMETERY_MAX_STONES)
  const cols = 8, dx = 0.19, dz = 0.3
  const rows = Math.ceil(n / cols)
  // a quiet plot of darker grass under the stones
  const plot = new THREE.Mesh(new THREE.PlaneGeometry(cols * dx + 0.4, rows * dz + 0.5), PLOT_MAT)
  plot.rotation.x = -Math.PI / 2
  plot.position.set(x, GROUND_Y - 0.02, z)
  plot.raycast = () => {}
  group.add(plot)
  for (let i = 0; i < n; i++) {
    const c = i % cols, r = Math.floor(i / cols)
    const s = headstone(i)
    s.position.set(x + (c - (cols - 1) / 2) * dx, GROUND_Y, z + (r - (rows - 1) / 2) * dz)
    s.traverse((o) => { o.raycast = () => {} }) // never block click-to-move
    group.add(s)
  }
  group.userData.stones = n
}

export function createCemetery({ scene }) {
  sceneRef = scene
  group = new THREE.Group()
  group.name = 'cemetery'
  scene.add(group)
  on(Events.GravesChange, rebuild)
  on(Events.KingdomChange, rebuild)
  on(Events.AccountStatePulled, rebuild)
  rebuild()
  if (import.meta.env?.DEV) window.__cemetery = { stones: () => group.userData.stones ?? 0, graves: getGraves, at: () => axialToWorld(CEMETERY_TILE.q, CEMETERY_TILE.r), rebuild }
  return { group, rebuild }
}
